import { motion } from "framer-motion";
import { RobotEyes } from "./RobotEyes";

interface Robot3DProps {
  cursor: {
    x: number;
    y: number;
  };
  passwordFocused: boolean;
  buttonHovered: boolean;
  loginSuccess: boolean;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

export const Robot3D = ({
  cursor,
  passwordFocused,
  buttonHovered,
  loginSuccess,
}: Robot3DProps) => {
  const rotateY =
    clamp((cursor.x - 0.5) * 18, -10, 10) + (buttonHovered ? 2 : 0);
  const rotateX =
    clamp((0.5 - cursor.y) * 14, -8, 8) + (passwordFocused ? 6 : 0);

  return (
    <div className="robot-stage" aria-hidden="true">
      <div className="robot-stage-glow robot-stage-glow-left" />
      <div className="robot-stage-glow robot-stage-glow-right" />

      <motion.div
        className={`robot-shell ${passwordFocused ? "is-focused" : ""} ${
          buttonHovered ? "is-hovered" : ""
        } ${loginSuccess ? "is-success" : ""}`}
        animate={{
          rotateX,
          rotateY,
          y: loginSuccess ? [-2, -8, -4] : [0, -6, 0],
          scale: loginSuccess ? 1.03 : buttonHovered ? 1.015 : 1,
        }}
        transition={{
          rotateX: { type: "spring", stiffness: 120, damping: 18 },
          rotateY: { type: "spring", stiffness: 120, damping: 18 },
          y: {
            duration: 4.8,
            repeat: Infinity,
            ease: "easeInOut",
          },
          scale: { duration: 0.3, ease: "easeOut" },
        }}
      >
        <div className="robot-orbit robot-orbit-one" />
        <div className="robot-orbit robot-orbit-two" />

        <div className="robot-head">
          <div className="robot-crown">
            <motion.div
              className="robot-antenna robot-antenna-core"
              animate={{
                y: [0, -1.5, 0],
                scale: [1, 1.03, 1],
                opacity: [0.9, 1, 0.9],
              }}
              transition={{
                duration: 2.6,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          </div>

          <div className="robot-face">
            <div className="robot-visor">
              <RobotEyes
                cursor={cursor}
                passwordFocused={passwordFocused}
                buttonHovered={buttonHovered}
                loginSuccess={loginSuccess}
              />
            </div>

            <motion.div
              className="robot-mouth"
              animate={{
                scaleX: loginSuccess ? 1.05 : 1,
                opacity: passwordFocused ? 0.94 : 0.78,
              }}
              transition={{ type: "spring", stiffness: 160, damping: 18 }}
            />
          </div>
        </div>

        <div className="robot-neck">
          <span className="robot-neck-ring" />
        </div>

        <div className="robot-body">
          <div className="robot-body-panel robot-body-panel-left" />
          <div className="robot-body-panel robot-body-panel-center">
            <span className="robot-core-line" />
          </div>
          <div className="robot-body-panel robot-body-panel-right" />
        </div>

        <div className="robot-arms">
          <span className="robot-arm robot-arm-left" />
          <span className="robot-arm robot-arm-right" />
        </div>

        <motion.div
          className="robot-aura"
          animate={{
            opacity: loginSuccess ? 1 : buttonHovered ? 0.9 : 0.6,
          }}
          transition={{ duration: 0.25 }}
        />
      </motion.div>
    </div>
  );
};
